import React from "react";
import { Clock, Hash, Info, Star, User } from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import type { OperacaoDiaPedidoRow } from "../../../services/painelService";
import {
  resolveSalaoProducaoLabels,
  SALAO_CARD_SHELL_CLASS,
} from "../../Painel/utils/origemPainel";
import { sequenciaShellClass } from "../utils/sequenciaUrgencia";
import { formatChegouHorario, formatParado } from "../utils/formatTempo";

interface SequenciaPedidoCardProps {
  row: OperacaoDiaPedidoRow;
  isPrioritario: boolean;
  onDetalhes: () => void;
}

export const SequenciaPedidoCard: React.FC<SequenciaPedidoCardProps> = ({
  row,
  isPrioritario,
  onDetalhes,
}) => {
  const emAndamento =
    row.etapa !== "finalizado" && row.etapa !== "cancelado";
  const isSalao = row.canalOrigem === "salao";
  const shellClass = isSalao
    ? SALAO_CARD_SHELL_CLASS
    : sequenciaShellClass(row.alertaNivel, row.etapa, isPrioritario);
  const salaoLabels = isSalao
      ? resolveSalaoProducaoLabels({
          mesaId: row.salaoMesaId,
          comandaId: row.salaoComandaId,
          identificador: row.salaoIdentificador,
          displayId: row.displayId,
        })
      : null;

  return (
    <div data-salao-card={isSalao ? "" : undefined} className={shellClass}>
      <div className="p-4">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-slate-700">
              <Hash size={16} />
              Pedido #{row.displayId}
            </span>
            {salaoLabels?.mesa ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-amber-900">
                {salaoLabels.mesa}
              </span>
            ) : null}
            {salaoLabels?.comandaLabel ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-300 bg-violet-50 px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-violet-800">
                {salaoLabels.comandaLabel}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {isPrioritario && (
              <Star
                size={16}
                className="fill-amber-400 text-amber-500"
                aria-label="Prioritário"
              />
            )}
            {isSalao ? (
              <span className="rounded-full border border-emerald-400 bg-emerald-600 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                Salão
              </span>
            ) : (
              <OrderTypeBadge orderType={row.orderType} withTooltip={false} />
            )}
          </div>
        </div>

        <div className="mb-2">
          <span
            className={`inline-block rounded-md px-2 py-1 text-xs font-semibold ${
              row.etapa === "finalizado"
                ? "bg-emerald-100 text-emerald-900"
                : row.etapa === "cancelado"
                  ? "bg-neutral-200 text-neutral-700"
                  : "bg-blue-50 text-blue-900"
            }`}
          >
            {row.etapaLabel}
          </span>
        </div>

        {row.resumoItens && (
          <p className="mb-3 line-clamp-2 text-sm font-medium leading-snug text-neutral-800 [[data-salao-card]_&]:text-white">
            {row.resumoItens}
          </p>
        )}

        <div className="space-y-1.5 text-sm text-neutral-600 [[data-salao-card]_&]:text-white/85">
          <div className="flex items-center gap-2">
            <Clock
              size={15}
              className="shrink-0 text-neutral-400 [[data-salao-card]_&]:text-white/70"
            />
            <span>
              Na fila:{" "}
              <strong className="tabular-nums text-neutral-800 [[data-salao-card]_&]:text-white">
                {formatChegouHorario(row.chegouEm)}
              </strong>
            </span>
          </div>

          {emAndamento &&
            row.paradoSegundos != null &&
            row.paradoSegundos >= 60 && (
              <div className="flex items-center gap-2">
                <Clock
                  size={15}
                  className="shrink-0 text-neutral-400 [[data-salao-card]_&]:text-white/70"
                />
                <span>
                  Parado há:{" "}
                  <strong className="tabular-nums text-neutral-800 [[data-salao-card]_&]:text-white">
                    {formatParado(row.paradoSegundos)}
                  </strong>
                </span>
              </div>
            )}

          {isSalao ? (
            salaoLabels?.cliente ? (
              <div className="flex items-center gap-2">
                <User
                  size={15}
                  className="shrink-0 text-neutral-400 [[data-salao-card]_&]:text-white/70"
                />
                <span className="truncate text-neutral-500 [[data-salao-card]_&]:text-white/80">
                  {salaoLabels.cliente}
                </span>
              </div>
            ) : !salaoLabels?.mesa && salaoLabels?.secondary ? (
              <div className="flex items-center gap-2">
                <User
                  size={15}
                  className="shrink-0 text-neutral-400 [[data-salao-card]_&]:text-white/70"
                />
                <span className="truncate">{salaoLabels.secondary}</span>
              </div>
            ) : null
          ) : (
            row.customerName?.trim() && (
              <div className="flex items-center gap-2">
                <User size={15} className="shrink-0 text-neutral-400" />
                <span className="truncate">{row.customerName.trim()}</span>
              </div>
            )
          )}

          {row.montadores?.trim() && (
            <div className="text-xs text-neutral-500 [[data-salao-card]_&]:text-white/70">
              Montador: {row.montadores.trim()}
            </div>
          )}

          {row.pizzasTotal > 0 && (
            <div className="text-xs text-neutral-500 [[data-salao-card]_&]:text-white/70">
              {row.pizzasNaFila > 0
                ? `${row.pizzasNaFila}/${row.pizzasTotal} pizza(s) na fila`
                : `${row.pizzasTotal} pizza(s)`}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onDetalhes}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 transition hover:bg-neutral-50"
        >
          <Info size={16} />
          Detalhes
        </button>
      </div>
    </div>
  );
};
